import React, { useEffect, useState } from 'react';
import {
  Upload,
  Loader2,
  CircleCheck,
  CircleX,
  CircleAlert,
  X,
} from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/Components/ui/dialog';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Card, CardContent } from '@/Components/ui/card';
import { cn } from '@/lib/utils';

import useCandidateUpload from './useCandidateUpload';

/**
 * CandidateUploadModal
 *
 * Wraps the `useCandidateUpload` hook to provide a recruiter-facing
 * dialog: pick files → start upload → see per-file progress + results.
 * On full success it auto-closes and reloads the page; on partial /
 * duplicate / failed it stays open with a toast-like banner.
 */
export function CandidateUploadModal({
  open,
  onClose,
  jobId,
  recruiterEmail,
  jobTitle,
  onSuccess,
}) {
  const [popup, setPopup] = useState({
    open: false,
    type: '',
    title: '',
    message: '',
    closable: true,
  });

  const {
    isUploading,
    uploadFilesStatus,
    overallProgress,
    selectedFiles,
    handleFileSelect,
    startUpload,
    resetUpload,
  } = useCandidateUpload({
    jobId,
    recruiterEmail,
    onSuccess,
    onComplete: (result) => {
      const { successCount, duplicateCount, failedCount, total } = result;

      if (successCount === total) {
        // 🎉 happy path — close the dialog, then reload to refresh listings
        setPopup({
          open: true,
          type: 'success',
          title: 'Upload Successful',
          message: `All ${successCount} resume${
            successCount > 1 ? 's' : ''
          } uploaded successfully.`,
          closable: false,
        });
        setTimeout(onClose, 1200);
        setTimeout(() => window.location.reload(), 3000);
        return;
      }

      if (successCount > 0) {
        setPopup({
          open: true,
          type: 'warning',
          title: 'Partial Upload',
          message: `${successCount} success · ${duplicateCount} duplicate · ${failedCount} failed`,
          closable: true,
        });
        return;
      }

      if (duplicateCount === total) {
        setPopup({
          open: true,
          type: 'warning',
          title: 'Duplicate Resumes',
          message: 'All selected resumes already exist.',
          closable: true,
        });
        return;
      }

      setPopup({
        open: true,
        type: 'error',
        title: 'Upload Failed',
        message: 'All resumes failed to upload. Please try again.',
        closable: true,
      });
    },
  });

  // Reset state every time the modal closes so the next opening is clean.
  useEffect(() => {
    if (!open) {
      resetUpload();
      setPopup({
        open: false,
        type: '',
        title: '',
        message: '',
        closable: true,
      });
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !o && !isUploading && onClose?.()}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-primary" />
            Upload Candidate Resume
            {selectedFiles.length > 1 ? 's' : ''}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Job: {jobTitle || jobId}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Result banner (toast-like) */}
          {popup.open && (
            <ResultBanner
              type={popup.type}
              title={popup.title}
              message={popup.message}
              closable={popup.closable}
              onClose={() => setPopup((p) => ({ ...p, open: false }))}
            />
          )}

          {!isUploading && uploadFilesStatus.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Select resume files first, then click <strong>Upload</strong>.
            </p>
          )}

          {/* File picker */}
          {!isUploading && (
            <>
              <input
                type="file"
                id="cu-file-input"
                accept=".pdf,.doc,.docx,.txt"
                multiple
                hidden
                onChange={handleFileSelect}
              />
              <label
                htmlFor="cu-file-input"
                className={cn(
                  'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border/60 bg-muted/30 px-6 py-10 text-center transition-colors hover:bg-muted/50',
                  selectedFiles.length > 0 && 'border-primary/40 bg-primary/5',
                )}
              >
                <Upload className="h-8 w-8 text-muted-foreground" />
                <span className="text-sm font-medium">
                  {selectedFiles.length > 0
                    ? `${selectedFiles.length} file(s) selected`
                    : 'Click to select resumes'}
                </span>
                <div className="flex gap-1.5">
                  <Badge variant="outline" className="text-[10px]">
                    PDF
                  </Badge>
                  <Badge variant="outline" className="text-[10px]">
                    DOC
                  </Badge>
                  <Badge variant="outline" className="text-[10px]">
                    DOCX
                  </Badge>
                  <Badge variant="outline" className="text-[10px]">
                    TXT
                  </Badge>
                </div>
              </label>
            </>
          )}

          {/* Overall progress */}
          {isUploading && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">Uploading files</span>
                <span className="font-mono text-xs text-muted-foreground">
                  {overallProgress}%
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{ width: `${overallProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Per-file status */}
          {uploadFilesStatus.length > 0 && (
            <div className="max-h-48 space-y-2 overflow-y-auto">
              {uploadFilesStatus.map((f, i) => (
                <FileRow key={i} status={f.status} name={f.name} />
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => !isUploading && onClose?.()}
            disabled={isUploading}
          >
            Cancel
          </Button>
          {!isUploading && selectedFiles.length > 0 && (
            <Button onClick={startUpload}>Upload</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function FileRow({ status, name }) {
  const map = {
    queued: { Icon: Loader2, tone: 'text-muted-foreground', label: 'Queued', spin: true },
    uploading: { Icon: Loader2, tone: 'text-blue-600', label: 'Uploading', spin: true },
    processing: { Icon: Loader2, tone: 'text-violet-600', label: 'Processing', spin: true },
    success: { Icon: CircleCheck, tone: 'text-emerald-600', label: 'Success' },
    failed: { Icon: CircleX, tone: 'text-destructive', label: 'Failed' },
    duplicate: { Icon: CircleAlert, tone: 'text-amber-600', label: 'Duplicate' },
  };
  const cfg = map[status] || map.queued;
  return (
    <div className="flex items-center justify-between gap-2 rounded-md border border-border/40 bg-card p-2.5">
      <div className="flex min-w-0 items-center gap-2">
        <cfg.Icon
          className={cn('h-4 w-4 shrink-0', cfg.tone, cfg.spin && 'animate-spin')}
        />
        <span className="truncate text-sm" title={name}>
          {name}
        </span>
      </div>
      <Badge variant="outline" className={cn('border-0 text-[10px]', cfg.tone)}>
        {cfg.label}
      </Badge>
    </div>
  );
}

function ResultBanner({ type, title, message, closable, onClose }) {
  const tones = {
    success: 'border-emerald-500/40 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200',
    error: 'border-destructive/40 bg-destructive/10 text-destructive',
    warning: 'border-amber-500/40 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200',
  };
  return (
    <Card className={cn('border', tones[type] || tones.warning)}>
      <CardContent className="flex items-start gap-3 p-3">
        <div className="flex-1">
          <p className="text-sm font-semibold">{title}</p>
          <p className="mt-0.5 text-xs opacity-90">{message}</p>
        </div>
        {closable && (
          <button
            onClick={onClose}
            className="rounded p-1 hover:bg-foreground/10"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </CardContent>
    </Card>
  );
}
